import React from "react";
import styled from "styled-components";
import { Modal, colors2, UI_BODY, UI_CAPTION } from "oolib";
import { ButtonGhost, ButtonPrimary } from "oolib/dist/v2/components/Buttons";
import {
  useDisconnectExternalConnection,
  useExternalConnectionStatus,
} from "@/utils/react-query-hooks/externalConnections";
import { useGetAllPublishedAndUnpublishedTpls } from "@/utils/react-query-hooks/tci";

/**
 * Confirmation modal for the Disconnect action.
 *
 * Two modes:
 *
 * 1. Self-contained (Integrations tab — the default). Pass `provider` + `name`
 *    and the modal owns the disconnect: it fires useDisconnectExternalConnection,
 *    invalidates the status query, and closes on success. Mounted by
 *    SM_Integrations when the user clicks Disconnect from the Settings modal's
 *    dataset-list header or the card's gear menu. Distinct from
 *    IntegrationSettingsModal so the page-level collab lock derivation
 *    (lockedProvider = settings || activate || disconnectConfirm) includes it —
 *    opening this modal acquires the per-integration lock, so a second user
 *    attempting any modal action while a disconnect is being confirmed is
 *    blocked.
 *
 * 2. Presentational (import-screen integration interfaces). Pass `onConfirm`
 *    (and optionally `isLoading`) and the caller owns the disconnect. The import
 *    interfaces disconnect via useOAuthConnection.handleDisconnect, which flips
 *    their own *local* connection state back to "Connect" and resets the picker —
 *    so they can't delegate the network call to this modal's mutation, only the
 *    confirm UI. When `onConfirm` is supplied the internal mutation is never run.
 *
 * Because one integration can feed several datasets, and disconnecting stops
 * ALL of their schedules, mode 1 names every affected dataset before the user
 * confirms. It reads that list itself rather than taking it as a prop, so every
 * caller gets the same warning without having to assemble it.
 */

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
`;

const DatasetList = styled.ul`
  margin: 0;
  padding-left: 20px;
`;

const FooterBar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 24px;
  border-top: 1px solid ${colors2.grey10};
`;

// onDisconnected: called after a successful self-contained disconnect, e.g. so
// an import popup that needs the connection can close too.
export const IntegrationDisconnectConfirmModal = ({ provider, name, onClose, onConfirm, isLoading: isLoadingProp, onDisconnected,
}) => {
  // Presentational mode when the caller passes onConfirm — it owns the
  // disconnect, so the internal mutation below is never triggered (useMutation
  // doesn't auto-run, so wiring it up unconditionally is harmless).
  const presentational = typeof onConfirm === "function";

  const { mutate: disconnect, isLoading: mutationLoading } =
    useDisconnectExternalConnection(provider, {
      providerName: name,
    });

  // The datasets that will stop syncing. The status query is disabled without a
  // provider, so presentational callers pay nothing for this.
  const { data: conn } = useExternalConnectionStatus(provider);
  const { data: allTpls } = useGetAllPublishedAndUnpublishedTpls();
  const datasetTitles = (conn?.links || []).map((link) => {
    const tpl = allTpls?.find((t) => t.kp_content_type === link.contentType);
    return tpl?.general?.content?.title || link.contentType;
  });

  const isLoading = presentational ? !!isLoadingProp : mutationLoading;

  const handleConfirm = () => {
    if (presentational) {
      onConfirm();
    } else {
      disconnect(undefined, {
        onSuccess: () => {
          onClose();
          onDisconnected?.();
        },
      });
    }
  };

  return (
    <Modal
      fitToContentHeight={true}
      desktopWidth="480px"
      onClose={isLoading ? undefined : onClose}
      title={`Disconnect ${name}?`}
    >
      <Body>
        <UI_BODY>
          {name} will be disconnected from this platform. You'll need to
          reconnect via OAuth before importing from it again.
        </UI_BODY>
        {datasetTitles.length > 0 && (
          <>
            <UI_BODY>
              {datasetTitles.length === 1
                ? "This dataset will stop syncing:"
                : `These ${datasetTitles.length} datasets will stop syncing:`}
            </UI_BODY>
            <DatasetList>
              {datasetTitles.map((title) => (
                <li key={title}>
                  <UI_BODY>{title}</UI_BODY>
                </li>
              ))}
            </DatasetList>
            <UI_CAPTION style={{ color: colors2.grey60 }}>
              The datasets and everything already imported into them remain. Only
              the connection and its schedules are removed, so reconnecting means
              setting each dataset up again.
            </UI_CAPTION>
          </>
        )}
      </Body>
      <FooterBar>
        <ButtonGhost onClick={onClose} disabled={isLoading}>Cancel</ButtonGhost>
        <ButtonPrimary onClick={handleConfirm} isLoading={isLoading}>
          Disconnect
        </ButtonPrimary>
      </FooterBar>
    </Modal>
  );
};

export default IntegrationDisconnectConfirmModal;
